import { Trans, useLingui } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'designSystem/components/Select'
import { Spinner } from 'designSystem/components/Spinner'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import {
  CoverageFilter,
  coverageFilterSchema,
} from 'features/sentinels/coverage'
import {
  ALL_SOURCES,
  DataFilter,
  dataFilterSchema,
} from 'features/timeSeries/components/panes/tagFilters'
import { Check, X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const ChipRemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  margin-left: 0.125rem;
  padding: 0.125rem;
  border-radius: 0.25rem;
  cursor: pointer;
  color: var(--muted-foreground);

  &:hover {
    color: var(--foreground);
    background: var(--accent);
  }

  &:focus-visible {
    outline: none;
    color: var(--foreground);
    box-shadow: 0 0 0 2px var(--ring);
  }
`

interface KeywordChipProps {
  label: string
  onRemove: () => void
}

export const KeywordChip: React.FC<KeywordChipProps> = ({
  label,
  onRemove,
}) => {
  const { t } = useLingui()
  return (
    <Badge variant='outlineMuted'>
      {label}
      <ChipRemoveButton
        type='button'
        aria-label={t`Remove ${label}`}
        onClick={onRemove}>
        <X size={10} />
      </ChipRemoveButton>
    </Badge>
  )
}

interface TagFilterRowProps {
  sources: string[]
  source: string
  onSourceChange: (source: string) => void
  dataFilter: DataFilter
  onDataFilterChange: (filter: DataFilter) => void
  isCheckingAvailability: boolean
  /** Rail mode: the select fills the row instead of a fixed dialog width. */
  stretchSelect?: boolean
  coverageFilter?: CoverageFilter
  onCoverageFilterChange?: (filter: CoverageFilter) => void
}

export const TagFilterRow: React.FC<TagFilterRowProps> = ({
  sources,
  source,
  onSourceChange,
  dataFilter,
  onDataFilterChange,
  isCheckingAvailability,
  stretchSelect = false,
  coverageFilter,
  onCoverageFilterChange,
}) => {
  const { t } = useLingui()
  return (
    <>
      <Select value={source} onValueChange={onSourceChange}>
        <SelectTrigger
          aria-label={t`Source`}
          size='xs'
          width={stretchSelect ? 'full' : 'fixed'}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent position='popper' align='start'>
          <SelectItem value={ALL_SOURCES}>
            <Trans>All sources</Trans>
          </SelectItem>
          {sources.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToggleGroup
        type='single'
        size='sm'
        variant='outline'
        value={dataFilter}
        onValueChange={(value) => {
          const parsed = dataFilterSchema.safeParse(value)
          if (parsed.success) onDataFilterChange(parsed.data)
        }}>
        <ToggleGroupItem value='all' aria-label={t`All tags`}>
          <Trans>All</Trans>
        </ToggleGroupItem>
        <ToggleGroupItem value='has' aria-label={t`Tags with recent data`}>
          <Trans>Has data</Trans>
        </ToggleGroupItem>
        <ToggleGroupItem
          value='missing'
          aria-label={t`Tags without recent data`}>
          <Trans>No data</Trans>
        </ToggleGroupItem>
      </ToggleGroup>
      {coverageFilter !== undefined && onCoverageFilterChange && (
        <ToggleGroup
          type='single'
          size='sm'
          variant='outline'
          value={coverageFilter}
          onValueChange={(value) => {
            const parsed = coverageFilterSchema.safeParse(value)
            if (parsed.success) onCoverageFilterChange(parsed.data)
          }}>
          <ToggleGroupItem value='all' aria-label={t`Any monitor coverage`}>
            <Trans>All</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem
            value='active'
            aria-label={t`Tags with an active monitor`}>
            <Trans>Active</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem
            value='experimenting'
            aria-label={t`Tags with experiments but no active monitor`}>
            <Trans>Exp</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem value='none' aria-label={t`Unmonitored tags`}>
            <Trans>None</Trans>
          </ToggleGroupItem>
        </ToggleGroup>
      )}
      {isCheckingAvailability && <Spinner />}
    </>
  )
}

const CheckMarkBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  border-radius: 0.25rem;
  border: 1px solid
    ${({ $checked }) => ($checked ? 'var(--primary)' : 'var(--input)')};
  background: ${({ $checked }) =>
    $checked ? 'var(--primary)' : 'transparent'};
  color: var(--primary-foreground);
`

/* Row selection state lives on the row button (aria-pressed); a nested
   checkbox control would be a button inside a button. */
export const CheckMark: React.FC<{ checked: boolean }> = ({ checked }) => (
  <CheckMarkBox aria-hidden $checked={checked}>
    {checked && <Check size={12} />}
  </CheckMarkBox>
)
